/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import PropTypes from 'prop-types';
import InputTextLine from '../../../juristec-ui/core/InputTextLine';
import Select from '../../../juristec-ui/core/Select';
import Button from '../../../juristec-ui/core/Button';
import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';

import { verifyEmail } from '../../../juristec-ui/utils/validators/inputTextValidators';

import {
  AddUserContainer,
  ItemContainer,
} from './styled/AddUser.styled';

const adminInfo = { id: 'super', label: 'Proprietário', value: 'super' };
const scientistInfo = { id: 'scientist', label: 'Cientista', value: 'scientist' };
const guestInfo = { id: 'guest', label: 'Convidado', value: 'guest' };

const addUserDefaultPropUsersEmails = [];
const addUserDefaultPropScientistEmails = [{}];

const addUserDefaultPropEditAttributes = {
  role: '',
  scientist: '',
  scientistName: '',
  group: '',
};

const addUserDefaultPropTourContext = { tourOpen: false, nextStep: () => {} };

/**
 * A modal to handle the addition of new users to the instance
 */
const AddUser = ({
  user,
  userIsAdmin = false,
  usersEmails = addUserDefaultPropUsersEmails,
  scientistEmails = addUserDefaultPropScientistEmails,
  submitData,
  hide,
  fromAdmin = false,
  groupsList,
  isEdit = false,
  editAttributes = addUserDefaultPropEditAttributes,
  edittingSelf = false,
  tourContext = addUserDefaultPropTourContext,
}) => {
  const [emailInput, setEmailInput] = useState({ value: '', error: true, errorMsg: '' });
  const [selected, setSelected] = useState(() => (!isEdit ? guestInfo : {
    id: editAttributes.role,
    value: editAttributes.role,
    label: editAttributes.role,
  }));
  const [scientist, setScientist] = useState(() => (!isEdit ? {
    value: '',
    label: 'Selecione',
    id: 'select-label',
  } : {
    value: editAttributes?.scientist || '',
    label: editAttributes?.scientistName || '',
    id: editAttributes.scientist,
  }));
  const [userGroups, setUserGroup] = useState(() => (!isEdit ? []
    : editAttributes.groups ? editAttributes.groups.map((group) => ({
      id: group,
      label: groupsList.find((g) => g.id === group)?.label,
      value: group,
    })) : []
  ));

  let profileOptions = [];
  if (user?.role === 'super' || userIsAdmin) {
    profileOptions = [
      adminInfo,
      guestInfo,
      scientistInfo,
    ];
  } else if (user?.role === 'scientist') {
    profileOptions = [
      guestInfo,
      scientistInfo,
    ];
  } else {
    profileOptions = [
      guestInfo,
    ];
  }

  const emailInputHandle = (value) => {
    const val = value.split(' ').filter((v) => v).join('');
    const msg = verifyEmail(val, true);
    setEmailInput({
      value: val,
      error: msg.length !== 0,
      errorMsg: msg,
    });
  };

  const handleUserGroup = (val) => {
    if (typeof (val) === 'object') {
      setUserGroup(val);
    }
  };

  const handleSubmit = () => {
    if (!isEdit) {
      const emailInUse = usersEmails.includes(emailInput.value);
      if (emailInUse) {
        setEmailInput({
          value: emailInput.value,
          error: true,
          errorMsg: 'Já existe um usuário adicionado com este email',
        });
      } else if (!emailInput.error) {
        submitData({
          email: emailInput.value,
          role: selected.value,
          groups: userGroups.map((group) => group.value),
          scientist: selected.value !== 'guest' ? '' : scientist.id,
        });
      } else {
        emailInputHandle(emailInput.value);
      }
      return;
    }
    const newObject = {
      role: selected.value,
      scientist: selected.value !== 'guest' ? '' : scientist.value,
      groups: userGroups.map((group) => group.value),
    };
    submitData(newObject);
  };

  return (
    <>
      <AddUserContainer>
        {!isEdit && (
          <ItemContainer>
            <InputTextLine
              label="E-mail do usuário"
              placeholder="novousuario@email.com"
              errorMessage={emailInput.errorMsg}
              error={emailInput.errorMsg.length > 0}
              onChange={(e) => emailInputHandle(e.target.value)}
              value={emailInput.value}
            />
          </ItemContainer>
        )}
        <ItemContainer
          style={edittingSelf ? {
            cursor: 'not-allowed',
            background: 'transparent',
            zIndex: '1050',
          } : {}}
          title={edittingSelf ? 'Não é possível editar seu próprio perfil' : 'Selecionar perfil'}
        >
          <Select
            selectLabel="Perfil do usuário"
            options={profileOptions}
            isDisabled={edittingSelf}
            fullWidth
            placement="start"
            value={selected}
            style={edittingSelf ? {
              background: 'transparent',
            } : {}}
            onChange={(val) => setSelected(val)}
            atModal
          />
        </ItemContainer>
        {!fromAdmin && (
          <ItemContainer>
            <Select
              selectLabel="Selecione um ou mais grupos (opcional)"
              placeholder=""
              isSearchable
              fullWidth
              options={groupsList}
              placement="start"
              isMult
              value={userGroups}
              onChange={handleUserGroup}
              tourContext={tourContext}
              atModal
            />
          </ItemContainer>
        )}
        {selected.value === 'guest' && (
          <ItemContainer>
            <Select
              selectLabel="Cientista responsável"
              options={scientistEmails}
              fullWidth
              placement="start"
              value={scientist}
              onChange={(val) => setScientist(val)}
              atModal
            />
          </ItemContainer>
        )}
      </AddUserContainer>
      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={hide}
          variant="outlined"
          size="large"
        >
          Cancelar
        </Button>
        <Button
          style={{ margin: '5px' }}
          onClick={() => handleSubmit()}
          size="large"
          disabled={(!isEdit ? emailInput.error : false) || (fromAdmin ? selected?.value === 'guest' && scientist?.value === '' : false)}
        >
          {!isEdit ? 'Adicionar' : 'Salvar'}
        </Button>
      </ActionsGroup>
    </>
  );
};

AddUser.propTypes = {
  /**
   * Current user info
   */
  user: PropTypes.shape({
    role: PropTypes.string.isRequired,
    isAdmin: PropTypes.bool.isRequired,
    id: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  /**
   * Array of users emails already added
   */
  usersEmails: PropTypes.arrayOf(PropTypes.string),
  /*
  * Array of scientist emails
  */
  scientistEmails: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  /**
   * A function that sends the new data back to the parent
   */
  submitData: PropTypes.func.isRequired,
  /**
   * Function to close the modal
   */
  hide: PropTypes.func.isRequired,
  /**
   * A boolean that shows if the call of this modal comes from the admin page
   */
  fromAdmin: PropTypes.bool,
  /**
   * A boolean that indicates if the modal is to create or edit a user
   */
  isEdit: PropTypes.bool,
  /**
   * attributes from user to be edited
   */
  editAttributes: PropTypes.shape({
    role: PropTypes.string,
    scientist: PropTypes.string,
    scientistName: PropTypes.string,
    group: PropTypes.string,
  }),
  userIsAdmin: PropTypes.bool,
  groupsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  edittingSelf: PropTypes.bool,
  tourContext: PropTypes.shape({
    tourOpen: PropTypes.bool,
    nextStep: PropTypes.func,
  }),
};

export default AddUser;
